import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { ReadingFormatSelector } from '@/components/ReadingFormatSelector';
import { saveDefaultFormat, getUserDefaultFormat, ReadingFormat } from '@/lib/readingFormatUtils';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowRight } from 'lucide-react';

function OnboardingReadingFormat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [selectedFormat, setSelectedFormat] = useState<ReadingFormat | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const returnTo = searchParams.get('returnTo');

  // Load existing preference if any
  useEffect(() => {
    async function loadExistingFormat() {
      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const existingFormat = await getUserDefaultFormat(user.id);
        if (existingFormat) {
          setSelectedFormat(existingFormat);
        } else {
          // Default to print if no preference set
          setSelectedFormat('print');
        }
      } catch (error) {
        console.error('Error loading format:', error);
        setSelectedFormat('print');
      } finally {
        setLoading(false);
      }
    }

    loadExistingFormat();
  }, [user, navigate]);

  const handleContinue = async () => {
    if (!user || !selectedFormat) return;

    try {
      setSaving(true);
      await saveDefaultFormat(user.id, selectedFormat);
      
      toast({
        title: "Preference saved",
        description: "Your default reading format has been saved."
      });

      // Navigate back to where we came from, or to home
      navigate(returnTo || '/');
    } catch (error) {
      console.error('Error saving format:', error);
      toast({
        title: "Error",
        description: "Failed to save your preference. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate(returnTo || '/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full max-w-full overflow-x-hidden">
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="card-elevated">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">How Do You Usually Read?</CardTitle>
              <CardDescription>
                This helps us personalize your experience. You can always change this later or specify a different format for individual books.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <ReadingFormatSelector
                value={selectedFormat}
                onChange={setSelectedFormat}
                label="Choose your primary reading format:"
              />

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  disabled={saving}
                  className="flex-1"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleContinue}
                  disabled={!selectedFormat || saving}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

export default OnboardingReadingFormat;
